import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import {
	Grid,
	Container,
	useTheme,
	useMediaQuery,
	Typography,
	// IconButton,
} from '@material-ui/core/';

import { TEST_ID_HARDCODED_DASHBOARD } from '../routes';
import { UserContext } from '../context/UserState';
import { setJwt } from '../services/httpService';

import TilePotentielleErsparnis from '../components/tiles/TilePotentielleErsparnis';
import GraphContainer from '../components/flow-graph/GraphContainer';
import { getLastgangDataResults } from '../services/lastgangService';
import LoadingIndicator from '../components/common/LoadingIndicator';
import CardWrapper from '../components/common/CardWrapper';
import DashboardPagnation from '../components/dashboard/DashboardPagnation';
import TileStorageCab from '../components/tiles/TileStorageCab';
import TileNetzentgeltOptimierung from '../components/tiles/TileNetzentgeltoptimierung';
import DialogDashboardTiles from '../components/dashboard/DialogDashboardTiles';
import BarStepper from '../components/dashboard/BarStepper';
import DashboardSwitch from '../components/dashboard/DashboardSwitch';
import TileEigenverbrauch from '../components/tiles/TileEigenverbrauch';
import TilePotentielleErsparnisEigenverbrauch from '../components/tiles/TilePotentielleErsparnisEigenverbrauch';
import StickyAppBar from '../components/common/StickyAppBar';
// import IconCalendar from '../assets/icon-calendar.svg';
// import IconExport from '../assets/icon-export.svg';

const Root = styled.main`
	.MuiToolbar-root {
		h5,
		h6 {
			font-weight: bold;
		}

		h5 {
			font-size: 24px;
		}

		h6 {
			margin-right: 20px;
			font-size: 16px;
			text-transform: uppercase;
		}

		.toolbar {
			display: flex;
			align-items: center;

			& > *:not(:first-child) {
				margin-left: 5px;
			}

			img {
				height: 24px;
				cursor: pointer;
			}
		}
	}
`;

const ContainerDashboardTiles = styled.div`
	margin: 40px 0;
`;

const PageDashboard = () => {
	const theme = useTheme();
	const matchBreakpointSmall = useMediaQuery(theme.breakpoints.up('sm'));
	const [dashboardData, setDashboardData] = useState(null);
	const [potentielleErsparnis, setPotentielleErsparnis] = useState(250000);
	const [co2SavingTile, setC02SavingTile] = useState(105);
	const [headlineNavigation, setHeadlineNavigation] = useState({
		projectName: 'Projektname',
		branchLocation: 'Projektstandort',
	});
	const [isLoading, setIsLoading] = useState(true);
	const { pathname: location } = useLocation();
	const defaultUserIDbyURL = location.split('/')[2];
	const [userIDbyURL, setUserIDbyURL] = useState(defaultUserIDbyURL);
	const [dashboardTilesIconValue, setDashboardTilesIconValue] = useState(0);
	const [openDashboardTileModal, setOpenDashboardTileModal] = useState(false);
	const [activeCase, setActiveCase] = useState(true); // true = Netzentgelte / False Eigenverbrauch
	const [streamGraphData, setStreamGraphData] = useState(null);
	const [fetchedData, setFetchedData] = useState(null);
	const { user } = useContext(UserContext);
	const [storagePark, setStoragePark] = useState(false);

	// Hardcoded Fallback of the URL - when ID is not given and no data will be fetched
	if (userIDbyURL === undefined) {
		const fallback = TEST_ID_HARDCODED_DASHBOARD.split('/')[1];
		setUserIDbyURL(fallback);
	}

	useEffect(() => {
		window.scrollTo(0, 0);

		if (!user.isLoggedIn) {
			// prettier-ignore
			/**
			 * If the user is not logged in we will set an Auth token for the anonymous user.
			 * This allows the user to refresh the page and session and to come back later.
			 * */
			setJwt(localStorage.getItem('anonymousLastgangId'));
		}

		const fetchData = async () => {
			/* eslint-disable no-unneeded-ternary */
			getLastgangDataResults(userIDbyURL)
				.then((res) => {
					setFetchedData(res.data);
					setDashboardData(res.data.gridTariffs.results);
					setStreamGraphData([
						res.data.gridTariffs.weekly,
						res.data.selfConsumption.weekly,
					]);
					setPotentielleErsparnis(res.data.gridTariffs.results.savings);
					setIsLoading(false);
					setStoragePark(
						res.data.gridTariffs.results.battery === 'storage.cab'
							? false
							: true
					);

					if (res.data.project) {
						setHeadlineNavigation({
							projectName: res.data.project,
							branchLocation: res.data.location,
							projectYear: res.data.year,
						});
					}
				})
				.catch(() => {
					setIsLoading(true);
				});
		};

		fetchData();
	}, [userIDbyURL, user.isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (fetchedData === null) {
			return;
		}
		designDatabyCase();
	}, [activeCase]); // eslint-disable-line react-hooks/exhaustive-deps

	const designDatabyCase = () => {
		/* eslint-disable no-unneeded-ternary */
		switch (activeCase) {
			case true:
				setDashboardData({ ...fetchedData.gridTariffs.results });
				setPotentielleErsparnis(fetchedData.gridTariffs.results.savings);
				setStoragePark(
					fetchedData.gridTariffs.results.battery === 'storage.cab'
						? false
						: true
				);
				break;
			case false:
				setDashboardData({ ...fetchedData.selfConsumption.results });
				setPotentielleErsparnis(fetchedData.selfConsumption.results.savings);
				setC02SavingTile(fetchedData.selfConsumption.results.co2savings);
				setStoragePark(
					fetchedData.selfConsumption.results.battery === 'storage.cab'
						? false
						: true
				);
				break;
			default:
				break;
		}
	};

	let Component = null;

	switch (activeCase) {
		case true:
			Component = (
				<Grid container spacing={!matchBreakpointSmall ? 8 : 3}>
					<Grid item sm={12} md={4}>
						<TileNetzentgeltOptimierung
							toggleModal={() => {
								setDashboardTilesIconValue(0);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
					<Grid item sm={12} md={4}>
						<TileStorageCab
							isStoragePark={storagePark}
							toggleModal={() => {
								setDashboardTilesIconValue(1);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
					<Grid item sm={12} md={4}>
						<TilePotentielleErsparnis
							data={potentielleErsparnis}
							toggleModal={() => {
								setDashboardTilesIconValue(2);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
				</Grid>
			);
			break;
		case false:
			Component = (
				<Grid container spacing={!matchBreakpointSmall ? 8 : 3}>
					<Grid item sm={12} md={4}>
						<TileEigenverbrauch
							toggleModal={() => {
								setDashboardTilesIconValue(0);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
					<Grid item sm={12} md={4}>
						<TileStorageCab
							isStoragePark={storagePark}
							toggleModal={() => {
								setDashboardTilesIconValue(1);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
					<Grid item sm={12} md={4}>
						<TilePotentielleErsparnisEigenverbrauch
							data={potentielleErsparnis}
							co2Data={co2SavingTile}
							toggleModal={() => {
								setDashboardTilesIconValue(2);
								setOpenDashboardTileModal(true);
							}}
						/>
					</Grid>
				</Grid>
			);
			break;

		default:
			break;
	}

	const { projectName, branchLocation, projectYear } = headlineNavigation;

	return (
		<Root>
			<Container maxWidth="lg">
				{isLoading ? (
					<CardWrapper>
						<LoadingIndicator />
					</CardWrapper>
				) : (
					<>
						{/* Dashboard Switch */}
						<DashboardSwitch
							activeSwitchState={() => setActiveCase(!activeCase)}
						/>

						{/* Breadcrumbs */}
						<DashboardPagnation
							projectName={projectName}
							branchLocation={branchLocation}
							projectYear={projectYear}
						/>

						{/* Appbar */}
						<StickyAppBar>
							<Typography variant="h5">metis.light</Typography>
							<div className="toolbar">
								<Typography variant="h6">
									Lastgang {projectYear}
								</Typography>
								{/* <IconButton>
									<img
										src={IconCalendar}
										className="calenderIconMetis"
										alt="metislightbarIcons"
									/>
								</IconButton>
								<IconButton>
									<img
										src={IconExport}
										className="ExportIconMetis"
										alt="metislightbarIcons"
									/>
								</IconButton> */}
							</div>
						</StickyAppBar>

						{/* Steps - Modal */}
						<DialogDashboardTiles
							isStoragePark={storagePark}
							open={openDashboardTileModal}
							close={() =>
								setOpenDashboardTileModal(!openDashboardTileModal)
							}
							selectedValue={dashboardTilesIconValue}
							currentSelectedValue={(e) => setDashboardTilesIconValue(e)}
							currentCase={activeCase}
							data={dashboardData}
						/>

						{/* Steps - Bar */}
						<ContainerDashboardTiles
							onClick={() => setOpenDashboardTileModal(true)}
						>
							<BarStepper
								selectedValue={(e) => setDashboardTilesIconValue(e)}
								currentSelected={dashboardTilesIconValue}
							/>
						</ContainerDashboardTiles>

						{/* Tiles */}
						{Component}

						{/* Graph */}
						{streamGraphData !== null && (
							<GraphContainer
								isStoragePark={storagePark}
								activeCase={activeCase}
								streamGraphData={streamGraphData}
								data={dashboardData}
							/>
						)}
					</>
				)}
			</Container>
		</Root>
	);
};

export default PageDashboard;
